import { EventMeta } from '../../../interfaces/meta.interface';

type FileAccessedFields = {
    ObjectId: string;
};

const FileAccessed: EventMeta<FileAccessedFields> = {
    title: 'SharePoint File Accessed',
    default: {
        description: 'SharePoint File Accessed',
        longDescription: 'A user or system account viewed a file.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // file accessed: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP accessed a SharePoint file`,
            longDescription: (fields) =>
                `A file on SharePoint was accessed from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to view sensitive information without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP.  allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. The action originated from a suspicious IP included in your allowed IP list. While the IP range itself is normally trusted, the specific IP has been flagged as suspicious, which makes the activity unusual. Review this access to confirm whether it was legitimate or potentially unauthorized, especially if sensitive files were involved.`,
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. The action originated from a suspicious IP located inside your allowed region. Although the region itself is normally trusted, the IP address has been flagged as suspicious. This makes the activity unusual and worth review to confirm whether the access was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. The action originated from a suspicious IP located in one of your allowed countries. Although the country itself is normally trusted under your access policies, the IP address has been flagged as suspicious. This combination makes the activity unusual. Review the access to confirm whether it was legitimate or potentially unauthorized, particularly if sensitive files were involved.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. The action originated from a suspicious IP that belongs to your allowed ASN list. While the ASN itself is trusted, the individual IP has been flagged as suspicious, which makes the activity unusual. Review this file access to confirm whether it was expected or potentially unauthorized, especially if sensitive documents were involved or if similar flagged IPs appear repeatedly.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) =>
                `An unapproved IP outside your allowed ASN, IPs, region, or country accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. The action originated from an unapproved IP outside all of your allowed ASNs, IPs, regions, and countries. This places the access fully outside your trusted boundaries. While this does not automatically mean malicious behaviour, it is unusual and should be reviewed carefully — especially if sensitive files were accessed or if similar activity recurs from unapproved sources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // file accessed: user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to access a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint with an account that does not belong to your organisation. The action originated from an unapproved IP outside your allowed list. While SharePoint file access is common, the combination of an external account and an unapproved IP makes this activity unusual. Review the access details to determine whether it was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // file accessed: microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. This was recorded as a Microsoft system operation event, with the IP address tied to the server responsible for synchronization. This entry reflects expected background activity and is informational in nature. No action is required unless this type of file access appears inconsistent with your configuration or expected usage patterns.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // file accessed
        {
            severity: 'info',
            description: (fields) => `A file was accessed on SharePoint`,
            longDescription: (fields) =>
                `A file was accessed on SharePoint. This is an informational record of routine file access activity. No unusual conditions were associated with this entry. No action is required unless the access was unexpected or aligns with other anomalies in user behaviour.`,
        },
    ],
};

export default FileAccessed;
